'use client';

import type { ProductFilterSchema } from '@coas/validators/payload';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { api } from 'trpc/react';
import type { RouterOutputs } from 'trpc/shared';
import IconChecked from '~/shared/assets/icons/checked.svg';
import { imgLoader } from '~/shared/lib';
import { Spinner } from '~/shared/ui/spinner';

type FilterBrandProps = {
    category: RouterOutputs['category']['getBySlug'] | null;
    list?: RouterOutputs['list']['getBySlug'] | null;
    form: UseFormReturn<ProductFilterSchema>;
};

export default ({ category, list, form }: FilterBrandProps) => {
    const searchParams = useSearchParams();

    const { register, watch } = form;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { brands: _brands, ...filter } = watch();

    const select = useCallback((data: RouterOutputs['brand']['getAll']) => {
        const coasBrand = data.find(
            brand =>
                brand.id === '62d0fb1b-04b0-4382-93ca-03947523db7f' ||
                brand.slug === 'coas-design' ||
                brand.name === 'COAS STUDIO',
        );

        const brandsWithoutCoas = data
            .filter(brand => brand.id !== coasBrand?.id)
            .sort((a, b) => a.name.localeCompare(b.name));

        if (!coasBrand) return brandsWithoutCoas;
        return [coasBrand, ...brandsWithoutCoas];
    }, []);

    const {
        data: brands,
        isLoading,
        isSuccess,
    } = api.brand.getAll.useQuery(
        {
            category: category?.item.id ?? null,
            list: list?.item.id ?? null,
            search: searchParams.get('search') ?? '',
            filter,
        },
        { select },
    );

    return (
        <>
            {isLoading && <Spinner className={'absolute inset-0 m-auto h-8 w-8'} />}
            <div className={'flex w-full flex-col'}>
                {isSuccess &&
                    brands.map(brand => {
                        return (
                            <label
                                key={brand.id}
                                htmlFor={brand.id}
                                className={'flex w-full items-center gap-x-4 py-2.5'}
                            >
                                <input
                                    id={brand.id}
                                    type={'checkbox'}
                                    value={brand.id}
                                    className={'peer -z-50 absolute opacity-0'}
                                    {...register('brands')}
                                />
                                <div className={'relative h-8 w-8'}>
                                    {!!brand.image && (
                                        <Image
                                            loader={imgLoader({ src: brand.image.key, width: 32 })}
                                            src={brand.image.key}
                                            placeholder={'blur'}
                                            blurDataURL={brand.image.lqip ?? undefined}
                                            className={'h-20 w-20 object-cover'}
                                            alt={brand.image.alt ?? ''}
                                            fill
                                        />
                                    )}
                                </div>
                                <span className={'flex-1 space-x-3 text-start'}>
                                    <span className={'text-18 text-neutral-950'}>{brand.name}</span>
                                    <span className={'font-300 text-16 text-neutral-500'}>
                                        ({brand._count.productBrands})
                                    </span>
                                </span>
                                <IconChecked
                                    className={'mb-1 h-5 w-5 stroke-transparent peer-checked:stroke-neutral-950'}
                                />
                            </label>
                        );
                    })}
            </div>
        </>
    );
};
